import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Tag, ExternalLink, ShoppingCart } from 'lucide-react';
import { useItem } from '../hooks';
import { LoadingSpinner, EmptyState } from '../components';
import type { PriceSource } from '../types';

export function ItemDetail() {
  const { id } = useParams<{ id: string }>();
  const { item, loading, error } = useItem(id || '');

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-ZA', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" text="Loading item details..." />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="space-y-4">
        <Link
          to="/items"
          className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to items
        </Link>
        <EmptyState
          title="Item not found"
          description="The item you're looking for doesn't exist or has been removed."
        />
      </div>
    );
  }

  const confidencePercent = Math.round((item.confidence_score || 0) * 100);

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <Link
        to="/items"
        className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to items
      </Link>

      {/* Header */}
      <div className="card p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{item.name}</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">{item.category}</p>

            {/* Tags */}
            {item.tags && item.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-200 text-sm rounded-full"
                  >
                    <Tag className="w-3.5 h-3.5" />
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Price Tiers */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Price Tiers</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
            <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-1">Budget (20th percentile)</p>
            <p className="text-3xl font-bold text-green-800 dark:text-green-200">
              {item.low_price
                ? new Intl.NumberFormat('en-ZA', {
                    style: 'currency',
                    currency: item.currency,
                  }).format(item.low_price)
                : 'N/A'}
            </p>
          </div>
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
            <p className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-1">Mid-Range (Median)</p>
            <p className="text-3xl font-bold text-amber-800 dark:text-amber-200">
              {item.medium_price
                ? new Intl.NumberFormat('en-ZA', {
                    style: 'currency',
                    currency: item.currency,
                  }).format(item.medium_price)
                : 'N/A'}
            </p>
          </div>
          <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
            <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">Premium (80th percentile)</p>
            <p className="text-3xl font-bold text-red-800 dark:text-red-200">
              {item.high_price
                ? new Intl.NumberFormat('en-ZA', {
                    style: 'currency',
                    currency: item.currency,
                  }).format(item.high_price)
                : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Data Quality */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Data Quality</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Confidence Score</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 dark:bg-dark-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-600 dark:bg-primary-400 rounded-full"
                    style={{ width: `${confidencePercent}%` }}
                  />
                </div>
                <span className="font-medium text-gray-900 dark:text-white">{confidencePercent}%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Data Source</span>
              <span className="font-medium text-gray-900 dark:text-white">{item.source}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Currency</span>
              <span className="font-medium text-gray-900 dark:text-white">{item.currency}</span>
            </div>
          </div>
        </div>

        {/* Timestamps */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Timeline</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Last Updated</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatDate(item.last_updated)}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Created</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatDate(item.created_at)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Retailer Prices */}
      {item.price_sources && item.price_sources.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Retailer Prices
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-700">
                  <th className="text-left py-2 pr-4 font-medium text-gray-500 dark:text-gray-400">Retailer</th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-500 dark:text-gray-400">Product</th>
                  <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">Price</th>
                  <th className="text-right py-2 pl-4 font-medium text-gray-500 dark:text-gray-400"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                {[...(item.price_sources as PriceSource[])]
                  .sort((a, b) => a.price - b.price)
                  .map((src, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-dark-700/50">
                      <td className="py-3 pr-4 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {src.retailer}
                      </td>
                      <td className="py-3 pr-4 text-gray-600 dark:text-gray-400 max-w-xs truncate">
                        {src.title}
                      </td>
                      <td className="py-3 text-right font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                        {new Intl.NumberFormat('en-ZA', {
                          style: 'currency',
                          currency: item.currency,
                        }).format(src.price)}
                      </td>
                      <td className="py-3 pl-4 text-right">
                        {src.url && (
                          <a
                            href={src.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
